import { t } from "@lingui/macro";
import React from "react";
import styled from "styled-components";
import { UnitGroup } from "~/applications/OfferSearch/Domain/UnitGroup";
import BathroomAmenity from "~/applications/OfferSearch/Ui/Amenities/BathroomAmenity/BathroomAmenity";
import BreakfastAmenity from "~/applications/OfferSearch/Ui/Amenities/BreakfastAmenity/BreakfastAmenity";
import CapacityAmenity from "~/applications/OfferSearch/Ui/Amenities/CapacityAmenity/CapacityAmenity";
import ConnectingDoorAmenity from "~/applications/OfferSearch/Ui/Amenities/ConnectingDoorAmenity/ConnectingDoorAmenity";
import KitchenAndCouchAmenity from "~/applications/OfferSearch/Ui/Amenities/KitchenAndCouchAmenity/KitchenAndCouchAmenity";
import RoomTypologyAmenity from "~/applications/OfferSearch/Ui/Amenities/RoomTypologyAmenity/RoomTypologyAmenity";
import SurfaceAmenity from "~/applications/OfferSearch/Ui/Amenities/SurfaceAmenity/SurfaceAmenity";
import InformationPill from "~/applications/OfferSearch/Ui/OfferDetails/InformationPill";
import { Fan, Hanger, Wifi } from "~/guidelines/Icon";
import Elevator from "~/guidelines/Icon/Elevator";
import NoElevator from "~/guidelines/Icon/NoElevator";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import {
  body1,
  buttonLarge,
  heading2,
  heading5,
  heading5Alt,
} from "~/guidelines/Typography";

const Wrapper = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing(0, 6, 6)};
  background-color: ${({ theme }) => theme.color.background.lightFirst};

  ${mediaQueries.md()} {
    padding: ${({ theme }) => theme.spacing(6, 6, 8)};
    background-color: ${({ theme }) => theme.color.background.lightSecond};
  }

  ${mediaQueries.lg()} {
    padding: ${({ theme }) => theme.spacing(8, 6, 12)};
  }

  ${mediaQueries.xxsOnly()} {
    padding: ${({ theme }) => theme.spacing(0, 3)};
  }
`;

const BackButtonContainer = styled.div`
  display: none;

  ${mediaQueries.md()} {
    display: block;
    position: absolute;
    top: ${({ theme }) => theme.spacing(6)};
    left: 5%;
  }

  ${mediaQueries.lg()} {
    top: ${({ theme }) => theme.spacing(6)};
    left: 5%;
  }
`;

const Container = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 775px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: ${({ theme }) => theme.spacing(5)};
  gap: ${({ theme }) => theme.spacing(6)};

  ${mediaQueries.md()} {
    gap: ${({ theme }) => theme.spacing(7)};
    align-items: center;
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  order: 2;

  ${mediaQueries.md()} {
    align-items: center;
    order: 0;
  }
`;

const ActionRow = styled.div`
  display: none;
  padding: 0;
  gap: 12px;

  ${mediaQueries.md()} {
    gap: 24px;
    display: flex;
    align-items: center;
  }
`;

const DestinationAndLoge = styled.h5`
  ${buttonLarge};
  margin: 0;
  padding: 0;

  ${mediaQueries.md()} {
    text-align: left;
    ${heading5Alt};
  }

  & em {
    font-style: normal;
    color: ${({ theme }) => theme.color.text.secondaryStrong};
  }
`;

const Title = styled.h2`
  ${heading5};
  margin: 0;
  padding: 0;

  ${mediaQueries.md()} {
    ${heading2};
    text-align: center;
  }
`;

const Amenities = styled.div`
  max-width: 775px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: ${({ theme }) => theme.spacing(4)};
  row-gap: ${({ theme }) => theme.spacing(3)};
  order: 4;

  ${mediaQueries.md()} {
    order: 0;
  }

  ${mediaQueries.lg()} {
    ${heading2};
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    column-gap: ${({ theme }) => theme.spacing(5)};
    row-gap: ${({ theme }) => theme.spacing(4)};
  }
`;

const Description = styled.div`
  white-space: pre-wrap;
  ${body1};
  text-align: justify;
  color: ${({ theme }) => theme.color.text.lightThird};
  margin: 0;
  padding: 0;
  order: 3;

  ${mediaQueries.md()} {
    order: 0;
    text-align: center;
  }
`;

export type RoomDescriptionHeaderProps = {
  unitGroup: UnitGroup;
  destinationName: string;
  backButton: React.ReactNode;
  bookingButton: React.ReactNode;
  explanationText?: React.ReactNode;
  accessibility?: {
    reducedMobilityRooms: boolean;
    elevator: boolean;
    stairliftPlatform: boolean;
  };
};

const RoomDescriptionHeader = ({
  unitGroup,
  destinationName,
  backButton,
  bookingButton,
  explanationText,
  accessibility,
}: RoomDescriptionHeaderProps) => {
  return (
    <Wrapper>
      <Container>
        <TitleRow>
          <DestinationAndLoge>
            {destinationName} <em>•</em> {unitGroup.loge.name}
          </DestinationAndLoge>
          <Title>{unitGroup.name}</Title>
        </TitleRow>
        <Amenities>
          <RoomTypologyAmenity
            variant="pill"
            nbRoom={unitGroup.numberOfRooms}
            hasLivingRoom={unitGroup.hasLivingRoom}
          />
          <KitchenAndCouchAmenity
            variant="pill"
            hasKitchen={unitGroup.hasKitchen}
            hasConvertibleCouch={unitGroup.hasConvertibleCouch}
          />
          <CapacityAmenity variant="pill" maxCapacity={unitGroup.maxCapacity} />
          <BreakfastAmenity variant="pill" />
          <BathroomAmenity
            variant="pill"
            numberOfBathrooms={unitGroup.numberOfBathrooms}
            numberOfReducedMobilityBathrooms={
              unitGroup.numberOfReducedMobilityBathrooms
            }
          />
          <SurfaceAmenity
            variant="pill"
            surfaceMin={unitGroup.surface.minimum}
            surfaceMax={unitGroup.surface.maximum}
          />
          <ConnectingDoorAmenity
            variant="pill"
            highlightConnectingDoor={unitGroup.highlightConnectingDoor}
          />
          <InformationPill
            icon={<Hanger />}
            text={t`Dressing & office space`}
          />
          <InformationPill icon={<Fan />} text={t`Air conditioning`} />
          <InformationPill icon={<Wifi />} text={t`Wi-Fi`} />
          {accessibility?.elevator && (
            <InformationPill icon={<Elevator />} text={t`Elevator`} />
          )}
          {accessibility?.elevator === false && (
            <InformationPill icon={<NoElevator />} text={t`No elevator`} />
          )}
        </Amenities>
        <Description>{unitGroup.description}</Description>
        {explanationText}
        <ActionRow>{bookingButton}</ActionRow>
      </Container>
      <BackButtonContainer>{backButton}</BackButtonContainer>
    </Wrapper>
  );
};

export default RoomDescriptionHeader;
